import * as React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { documentsApi } from '@/api/client';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Upload,
  FileSearch,
  CheckCircle,
  Search,
  Settings,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  LogOut,
  User,
  MessageSquare,
  Building2,
  ScrollText,
} from 'lucide-react';
import { useTheme } from '@/stores/themeStore';
import { useAuthStore } from '@/stores/authStore';

const navItems = [
  { id: 'dashboard', label: 'Tổng quan', icon: LayoutDashboard, path: '/' },
  { id: 'collection', label: 'Thu thập', icon: Upload, path: '/collection' },
  { id: 'review', label: 'Xem xét', icon: FileSearch, path: '/review' },
  { id: 'approval', label: 'Phê duyệt', icon: CheckCircle, path: '/approval' },
  { id: 'company', label: 'Cổng Công Ty', icon: Building2, path: '/company' },
  { id: 'search', label: 'Tra cứu', icon: Search, path: '/search' },
  { id: 'chat', label: 'AI Chat', icon: MessageSquare, path: '/chat' },
  { id: 'audit', label: 'Audit Logs', icon: ScrollText, path: '/audit' },
  { id: 'config', label: 'Cấu hình', icon: Settings, path: '/config' },
];

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function Sidebar({ collapsed = false, onToggle }: SidebarProps) {
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuthStore();

  // Fetch document counts for badges
  const { data: documentsData } = useQuery({
    queryKey: ['documents'],
    queryFn: async () => {
      const response = await documentsApi.list();
      return response.data;
    },
    staleTime: 1000 * 60 * 2, // 2 min cache
    refetchOnWindowFocus: true,
  });

  // Calculate counts by status
  const documents = Array.isArray(documentsData) ? documentsData : (documentsData?.documents || []);
  const counts = React.useMemo(() => {
    const result = { review: 0, approval: 0, total: documents.length };
    documents.forEach((doc: any) => {
      const status = (doc.document_status || doc.status || '').toUpperCase();
      if (status === 'PARSED') result.review++;
      if (status === 'REVIEWED') result.approval++;
    });
    return result;
  }, [documents]);

  // Dynamic nav items with badges
  const navItemsWithBadges = navItems.map(item => ({
    ...item,
    badge: item.id === 'review' ? counts.review :
           item.id === 'approval' ? counts.approval : 
           undefined
  }));

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 flex h-screen flex-col border-r border-gray-200 bg-white transition-all duration-300 dark:border-gray-800 dark:bg-gray-900',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b border-gray-200 px-4 dark:border-gray-800">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
              <span className="text-sm font-bold text-white">SME</span>
            </div>
            <span className="text-sm font-semibold text-gray-900 dark:text-white">
              Credit Rating
            </span>
          </div>
        )}
        {collapsed && (
          <div className="mx-auto flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <span className="text-xs font-bold text-white">S</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto p-3">
        <ul className="space-y-1">
          {navItemsWithBadges.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || 
              (item.path !== '/' && location.pathname.startsWith(item.path));

            return (
              <li key={item.id} className="relative">
                <NavLink
                  to={item.path}
                  className={cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-150',
                    isActive
                      ? 'bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400'
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white',
                    collapsed && 'justify-center px-2'
                  )}
                  title={collapsed ? `${item.label}${item.badge ? ` (${item.badge})` : ''}` : undefined}
                >
                  <Icon className={cn('h-5 w-5 shrink-0', isActive && 'text-brand-500')} />
                  {!collapsed && (
                    <>
                      <span className="flex-1">{item.label}</span>
                      {item.badge !== undefined && item.badge > 0 && (
                        <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-500 px-1.5 text-xs font-medium text-white">
                          {item.badge > 99 ? '99+' : item.badge}
                        </span>
                      )}
                    </>
                  )}
                </NavLink>
                {collapsed && item.badge !== undefined && item.badge > 0 && (
                  <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand-500 px-1 text-[10px] font-medium text-white">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom Section */}
      <div className="border-t border-gray-200 p-3 dark:border-gray-800">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition-all duration-150 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white',
            collapsed && 'justify-center px-2'
          )}
          title={collapsed ? (theme === 'dark' ? 'Light mode' : 'Dark mode') : undefined}
        >
          {theme === 'dark' ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
          {!collapsed && <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>}
        </button>

        {/* User & Logout */}
        {user && (
          <>
            <div
              className={cn(
                'mt-2 flex items-center gap-3 rounded-lg px-3 py-2.5',
                collapsed && 'justify-center px-2'
              )}
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:bg-brand-900/50 dark:text-brand-400">
                <User className="h-4 w-4" />
              </div>
              {!collapsed && (
                <div className="flex-1 overflow-hidden">
                  <p className="truncate text-sm font-medium text-gray-900 dark:text-white">
                    {user}
                  </p>
                </div>
              )}
            </div>
            <button
              onClick={logout}
              className={cn(
                'mt-1 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition-all duration-150 hover:bg-error-50 hover:text-error-600 dark:text-gray-400 dark:hover:bg-error-900/20 dark:hover:text-error-400',
                collapsed && 'justify-center px-2'
              )}
              title={collapsed ? 'Đăng xuất' : undefined}
            >
              <LogOut className="h-5 w-5" />
              {!collapsed && <span>Đăng xuất</span>}
            </button>
          </>
        )}

        {/* Collapse Toggle */}
        <button
          onClick={onToggle}
          className={cn(
            'mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition-all duration-150 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white',
            collapsed && 'justify-center px-2'
          )}
        >
          {collapsed ? (
            <ChevronRight className="h-5 w-5" />
          ) : (
            <>
              <ChevronLeft className="h-5 w-5" />
              <span>Thu gọn</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}

export default Sidebar;
